import { useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import Layout from '@/components/Layout';
import { getCompanyBySlug, companyPath, ROUTE_PATHS } from '@/lib/index';

function FadeUp({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 32 }} animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.9, delay, ease: [0.25, 0.46, 0.45, 0.94] }} className={className}>
      {children}
    </motion.div>
  );
}

export default function CompanyPortfolio() {
  const { slug } = useParams<{ slug: string }>();
  const company = getCompanyBySlug(slug ?? '');
  if (!company) return <Layout><div className="min-h-screen flex items-center justify-center"><Link to={ROUTE_PATHS.COMPANIES} className="btn-gold-outline">Back</Link></div></Layout>;

  return (
    <Layout activeCompanySlug={company.slug}>
      {/* Hero */}
      <section className="relative pt-40 pb-20 min-h-[50vh] flex items-center overflow-hidden bg-[#080808]">
        <div className="absolute inset-0">
          <img src={company.heroImage} alt={company.name} className="w-full h-full object-cover opacity-15" />
          <div className="absolute inset-0 bg-gradient-to-b from-[rgba(8,8,8,0.5)] to-[#080808]" />
        </div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-12">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1 }}>
            <p className="eyebrow mb-4">{company.name}</p>
            <h1 className="heading-display text-[clamp(3rem,7vw,5.5rem)] text-[#f5f5f5] max-w-3xl">
              Our <span className="text-gold italic">Portfolio</span>
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Portfolio editorial grid */}
      <section className="py-16 bg-[#080808]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          {/* Large featured */}
          <FadeUp className="mb-4">
            <div className="card-editorial h-[500px] lg:h-[620px] group">
              <img src={company.portfolioItems[0].image} alt={company.portfolioItems[0].title} className="w-full h-full object-cover" />
              <div className="card-overlay" />
              <div className="absolute inset-0 flex flex-col justify-end p-10 lg:p-14">
                <p className="eyebrow mb-2">{company.portfolioItems[0].category}</p>
                <h2 className="heading-display text-4xl lg:text-5xl text-[#f5f5f5] mb-2">{company.portfolioItems[0].title}</h2>
                <p className="text-[#f5f5f5]/50 text-sm">{company.portfolioItems[0].year}</p>
              </div>
            </div>
          </FadeUp>

          {/* Grid of rest */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {company.portfolioItems.slice(1).map((item, i) => (
              <FadeUp key={i} delay={i * 0.1}>
                <div className="card-editorial h-72 group">
                  <img src={item.image} alt={item.title} loading="lazy" />
                  <div className="card-overlay" />
                  <div className="absolute inset-0 flex flex-col justify-end p-6">
                    <p className="eyebrow text-[9px] mb-1">{item.category}</p>
                    <h3 className="heading-display text-xl text-[#f5f5f5]">{item.title}</h3>
                    <p className="text-[9px] tracking-[0.1em] text-[#f5f5f5]/40 mt-1">{item.year}</p>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#060606] text-center">
        <FadeUp>
          <p className="eyebrow mb-5">Ready to Create?</p>
          <h2 className="heading-display text-[clamp(2rem,4vw,3.5rem)] text-[#f5f5f5] mb-8">
            Let's Build Your Next Story
          </h2>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to={companyPath(company.slug) + '/contact'} className="btn-gold-fill">Start a Project</Link>
            <Link to={ROUTE_PATHS.HOME} className="btn-gold-outline">Back to Hub</Link>
          </div>
        </FadeUp>
      </section>
    </Layout>
  );
}
